import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Hint } from "@/components/devicePanel/Hint";
import { verdictErrorText } from "@/hooks/useDevicePanel";
import type { Editor, SharedText } from "@/types/jointReview";

const SAVE_DEBOUNCE_MS = 700;

type SaveState = { kind: "idle" } | { kind: "saving" } | { kind: "error"; text: string };

/**
 * A text both readers edit. Typing saves after a short pause (and on blur); the
 * other reader's edits arrive with the panel's poll. While this reader has
 * unsaved typing, a poll never replaces it: the field takes the server's text
 * only once its own last save is acknowledged with nothing typed since. A
 * read-only field (an observer's) shows the text and never saves.
 */
export function SharedTextField({
  value,
  onSave,
  label,
  placeholder,
  maxLength,
  rows,
  editors,
  readOnly,
}: {
  value: SharedText | null;
  onSave: (text: string) => Promise<unknown>;
  label: string;
  placeholder: string;
  maxLength: number;
  editors: Record<Editor, string>;
  readOnly: boolean;
  /** A textarea of this many rows; without, one row that grows with the text. */
  rows?: number;
}) {
  const serverText = value?.text ?? "";
  const [draft, setDraft] = useState(serverText);
  const [state, setState] = useState<SaveState>({ kind: "idle" });
  // The text this reader has typed and not yet seen acknowledged, if any.
  const pending = useRef<string | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const box = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (pending.current === null) setDraft(serverText);
  }, [serverText]);

  // A growing field fits its text (typed or polled) before paint, so a long
  // reason wraps onto new rows instead of running past the box.
  useLayoutEffect(() => {
    const field = box.current;
    if (rows || !field) return;
    field.style.height = "auto";
    field.style.height = `${field.scrollHeight}px`;
  }, [draft, rows]);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const save = async () => {
    window.clearTimeout(timer.current);
    const text = pending.current;
    if (text === null) return;
    setState({ kind: "saving" });
    try {
      await onSave(text);
      if (pending.current === text) pending.current = null;
      setState({ kind: "idle" });
    } catch (error) {
      setState({ kind: "error", text: verdictErrorText(error) });
    }
  };

  const change = (text: string) => {
    setDraft(text);
    pending.current = text;
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => void save(), SAVE_DEBOUNCE_MS);
  };

  const common = {
    "aria-label": label,
    value: draft,
    maxLength,
    placeholder,
    readOnly,
    onBlur: () => void save(),
    // The panel's keys (calls, Next) never fire while typing here.
    onKeyDown: (event: React.KeyboardEvent) => event.stopPropagation(),
    className:
      "w-full rounded-xs border border-border-strong bg-bg px-1.5 py-0.5 text-[11.5px] text-fg placeholder:text-fg-dim focus-visible:outline focus-visible:outline-1 focus-visible:outline-signal",
  };
  return (
    <div className="flex flex-col gap-0.5">
      <textarea
        {...common}
        ref={box}
        rows={rows ?? 1}
        className={rows ? common.className : `${common.className} resize-none overflow-hidden`}
        onChange={(event) => change(event.target.value)}
      />
      <span role="status" className="flex gap-2 text-[10px] text-fg-dim">
        {value?.edited_by && <span>Last edit: {editors[value.edited_by]}</span>}
        {state.kind === "saving" && <span>Saving…</span>}
        {state.kind === "error" && (
          <Hint label={state.text}>
            <span tabIndex={0} className="text-warn underline decoration-dotted underline-offset-2">
              Not saved<span className="sr-only">: {state.text}</span>
            </span>
          </Hint>
        )}
      </span>
    </div>
  );
}
